import React from "react";
import Logo from "../Logo";
import { SITE } from "../../data/mock";
import { eur, pct, dateIt, addDaysIt, quoteNumber, PAYMENT_MODES, isBattery, powerLabel } from "./format";
import {
  CGC_APPROVAL,
  CGC_ATTACHMENTS,
  CGC_INTRO,
  CGC_SECTIONS,
  CGC_TITLE,
  COMPANY_PEC,
  COMPANY_SIGNATORY,
  PRIVACY_PARAGRAPHS,
  PRIVACY_TITLE,
} from "./contractText";

/**
 * Preventivo in formato stampabile (Stampa -> Salva come PDF), sul modello dei
 * preventivi Excel del cliente, seguito da Condizioni Generali e informativa privacy.
 * Il codice in fondo contiene sigla commerciale e provvigione codificata:
 * il cliente lo legge come semplice riferimento.
 */

const INFO_LINE = "3509312938";
const STAMP_URL = `${process.env.PUBLIC_URL}/assets/timbro-energeide.png`;

const SERVICES = [
  "Sopralluogo Tecnico con Installatore",
  "Progettazione Esecutiva",
  "Direzione Lavori",
  "Installazione a Regola d'Arte",
  "Collaudo e Allaccio",
  "Pratiche: Allaccio, TERNA, GSE e ENEA",
];

const DISCLAIMER =
  "L'importo indicato costituisce il costo previsto dell'intervento. Eventuali lavorazioni aggiuntive, adeguamenti tecnici, modifiche richieste in fase di installazione o oneri sopravvenuti saranno contabilizzati separatamente, previa accettazione del committente.";

const NEGATIVE_SURVEY =
  "IN CASO DI ESITO NEGATIVO DEL SOPRALLUOGO PER QUALSIVOGLIA RAGIONE IL PRESENTE CONTRATTO SI ANNULLA AUTOMATICAMENTE E NULLA SARÀ DOVUTO DA AMBO LE PARTI";

// Etichetta di riga per tipo di prodotto (come nei preventivi Excel)
const LINE_LABELS = {
  modulo: "Pannelli",
  inverter_mono: "Inverter",
  inverter_tri: "Inverter",
  batteria: "Batteria",
  batteria_tri: "Batteria",
  colonnina: "Wall Box",
  backup: "Backup",
  extra: "Extra",
};

const num = (v) => (Number(v) || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });

// "impianto fotovoltaico 8 kW + 20 kWh chiavi in mano con Wall-Box"
const systemTitle = (calc, lines) => {
  const kwh =
    calc.battery_kwh ??
    lines.filter((l) => isBattery(l.category)).reduce((s, l) => s + l.power_kw * l.qty, 0);
  const extras = [];
  // Wallbox e backup possono essere inseriti anche come extra: si riconoscono dal nome
  if (lines.some((l) => l.category === "colonnina" || /wall\s*-?\s*box/i.test(l.name))) extras.push("Wall-Box");
  if (lines.some((l) => l.category === "backup" || /back\s*-?\s*up/i.test(l.name))) extras.push("Backup");
  if (calc.retrofit) {
    // Senza pannelli total_kw somma ogni prodotto (anche wallbox): qui contano solo gli inverter
    const invKw = lines
      .filter((l) => l.category === "inverter_mono" || l.category === "inverter_tri")
      .reduce((s, l) => s + l.power_kw * l.qty, 0);
    return [
      "Retrofit impianto fotovoltaico esistente",
      invKw > 0 ? `: inverter ${num(invKw)} kW` : "",
      kwh > 0 ? `${invKw > 0 ? " +" : ":"} batteria ${num(kwh)} kWh` : "",
      extras.length ? ` con ${extras.join(" e ")}` : "",
    ].join("");
  }
  return [
    `Impianto fotovoltaico ${num(calc.total_kw)} kW`,
    kwh > 0 ? ` + ${num(kwh)} kWh` : "",
    " chiavi in mano",
    extras.length ? ` con ${extras.join(" e ")}` : "",
  ].join("");
};

const Field = ({ label, children, className = "" }) => (
  <div className={`border border-gray-300 px-2.5 py-1.5 ${className}`}>
    <p className="text-[9px] font-bold uppercase tracking-wider text-gray-500">{label}</p>
    <p className="text-[13px] min-h-[1.25rem] break-words">{children}</p>
  </div>
);

const SignatureBlock = ({ place, date }) => (
  <div className="mt-8 grid grid-cols-2 gap-10 text-xs">
    <div>
      <p>Luogo, {place || "__________________"}</p>
      <p className="mt-1">Data, {date || "__________________"}</p>
      <p className="mt-6 font-semibold">Energeide s.r.l.</p>
      <p>{COMPANY_SIGNATORY.role}</p>
      <p>{COMPANY_SIGNATORY.name}</p>
      {/* Timbro e firma della società, sempre presenti sulla riga di firma */}
      <div className="border-b border-gray-400">
        <img src={STAMP_URL} alt="Timbro e firma Energeide s.r.l." className="h-20 w-auto max-w-none mix-blend-multiply" />
      </div>
    </div>
    <div className="self-end">
      <p className="font-semibold">Firma del Cliente</p>
      <div className="h-10 border-b border-gray-400" />
    </div>
  </div>
);

const PaymentSection = ({ payment, companyIban }) => {
  if (!payment) return null;
  const mode = PAYMENT_MODES[payment.mode];
  const hasDeposit = payment.mode !== "bonifico" && payment.deposit > 0;
  const modeText = hasDeposit && mode?.textDeposit ? mode.textDeposit : mode?.text;
  const cells =
    payment.mode === "bonifico"
      ? [
          ["Anticipo", eur(payment.deposit)],
          ["Saldo", eur(payment.balance)],
        ]
      : [
          ...(hasDeposit ? [["Anticipo", eur(payment.deposit)]] : []),
          ["Numero rate", payment.months],
          ["Importo rata", eur(payment.installment)],
        ];
  return (
    <section className="mt-5 break-inside-avoid">
      <p className="bg-[#0A1F44] text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5">
        Modalità di pagamento
      </p>
      <div className="grid grid-cols-[minmax(0,1fr)_auto] border border-t-0 border-gray-300">
        <p className="px-2.5 py-2 text-[13px]">{modeText}</p>
        <div className="flex border-l border-gray-300">
          {cells.map(([k, v]) => (
            <div key={k} className="px-3 py-1.5 border-l first:border-l-0 border-gray-300 text-right min-w-[6.5rem]">
              <p className="text-[9px] font-bold uppercase tracking-wider text-gray-500">{k}</p>
              <p className="text-[13px] font-semibold">{v}</p>
            </div>
          ))}
        </div>
      </div>
      {(payment.mode === "bonifico" || hasDeposit) && companyIban && (
        <p className="mt-1.5 text-[12px]">
          Bonifico a Energeide s.r.l. · IBAN <span className="font-mono font-semibold">{companyIban}</span>
        </p>
      )}
      {payment.mode !== "bonifico" && (
        <p className="mt-1 text-[11px] text-gray-500">
          Finanziamento Findomestic: {payment.months} rate mensili da {eur(payment.installment)}. Soggetto ad
          approvazione della finanziaria.
        </p>
      )}
    </section>
  );
};

const QuoteDocument = ({ quote }) => {
  const calc = quote.calc || {};
  const lines = calc.lines || [];
  const customItems = calc.custom_items || [];
  const customer = quote.customer || {};
  // Con il tasso zero il cliente vede un solo totale: quello che paga davvero
  // (anticipo + rate), con netto e IVA riportati su quel totale. Il finanziamento
  // con interessi e' tra Findomestic e il cliente: il totale resta il prezzo del preventivo.
  const pay = calc.payment;
  const financedTotal = pay && pay.mode !== "bonifico" && pay.plan !== "tan" ? pay.financed : 0;
  const withFinancing = financedTotal > 0;
  const grossTotal = withFinancing ? Math.round((pay.deposit + financedTotal) * 100) / 100 : quote.price_gross;
  const netTotal = withFinancing
    ? Math.round((grossTotal / (1 + quote.vat_pct / 100)) * 100) / 100
    : quote.price_net;
  const vatAmount = Math.round((grossTotal - netTotal) * 100) / 100;
  const date = dateIt(quote.created_at);

  return (
    <div className="quote-document text-[#0A1F44] mx-auto w-full max-w-[210mm]">
      {/* ------------------------------------------------ PREVENTIVO */}
      <article className="bg-white p-8 sm:p-10 shadow-sm border border-gray-100 print:shadow-none print:border-0 print:p-0">
        {/* div e non <header>: il CSS di stampa nasconde gli <header> del sito */}
        <div className="flex items-start justify-between gap-6 pb-5 border-b-2 border-[#0FB36B]">
          <div className="text-[12px] leading-relaxed text-gray-500">
            {/* max-w-none: il logo non va schiacciato (in stampa spariva) */}
            <Logo className="h-14 w-auto max-w-none mb-2" />
            <p>ENERGEIDE S.r.l. · {SITE.vat}</p>
            <p>{SITE.addressFull}</p>
            <p>{SITE.email} · info line {INFO_LINE}</p>
          </div>
          <div className="text-right text-[13px] text-gray-500 shrink-0">
            <p className="text-[11px] font-semibold tracking-[0.2em] text-[#0FB36B]">PREVENTIVO</p>
            <p className="text-2xl font-montserrat font-bold text-[#0A1F44]">{quoteNumber(quote)}</p>
            <p>Data: {date}</p>
            <p>Valido fino al: {addDaysIt(quote.created_at, quote.validity_days)}</p>
          </div>
        </div>

        <section className="mt-5 grid grid-cols-2 gap-6 text-[13px] text-gray-600 leading-relaxed">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Cliente</p>
            <p className="text-base font-bold text-[#0A1F44]">{customer.name}</p>
            {customer.address && <p>{customer.address}</p>}
            {(customer.zip || customer.city) && <p>{[customer.zip, customer.city].filter(Boolean).join(" ")}</p>}
            {customer.phone && <p>{customer.phone}</p>}
            {customer.email && <p>{customer.email}</p>}
            {customer.fiscal_code && <p>CF: {customer.fiscal_code}</p>}
            {customer.iban && <p>IBAN: {customer.iban}</p>}
          </div>
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Il tuo consulente</p>
            <p className="text-base font-bold text-[#0A1F44]">{quote.agent?.name}</p>
            {quote.agent?.phone && <p>{quote.agent.phone}</p>}
            {quote.agent?.email && <p>{quote.agent.email}</p>}
            {quote.region && <p>Installazione: {quote.region}</p>}
          </div>
        </section>

        <table className="mt-5 w-full text-[13px] border-collapse">
          <thead>
            <tr className="bg-[#0A1F44] text-white text-left">
              <th className="py-1.5 px-2.5 font-bold text-[11px] uppercase tracking-wider" colSpan={2}>
                Descrizione
              </th>
              <th className="py-1.5 px-2.5 font-bold text-[11px] uppercase tracking-wider text-right w-28">
                Quantità
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border border-gray-300">
              <td className="py-2 px-2.5 font-bold" colSpan={3}>
                {systemTitle(calc, lines)}
              </td>
            </tr>
            {SERVICES.map((s) => (
              <tr key={s} className="border border-gray-300">
                <td className="py-1 px-2.5" colSpan={2}>{s}</td>
                <td className="py-1 px-2.5 text-right">Inclusa</td>
              </tr>
            ))}
            {lines.map((l) => (
              <tr key={`${l.product_id}|${l.listino_id || ""}`} className="border border-gray-300">
                <td className="py-1 px-2.5 w-32 font-semibold">{LINE_LABELS[l.category] || "Extra"}</td>
                <td className="py-1 px-2.5">
                  {l.name}
                  {isBattery(l.category) && powerLabel(l) && !/kwh/i.test(l.name || "")
                    ? ` ${powerLabel(l)}`
                    : ""}
                </td>
                <td className="py-1 px-2.5 text-right">{num(l.qty)}</td>
              </tr>
            ))}
            {customItems.map((c, i) => (
              <tr key={`c${i}`} className="border border-gray-300">
                <td className="py-1 px-2.5 w-32 font-semibold">Extra</td>
                <td className="py-1 px-2.5">{c.description}</td>
                <td className="py-1 px-2.5 text-right">{num(c.qty)}</td>
              </tr>
            ))}
            <tr className="border border-gray-300">
              <td className="py-1 px-2.5" colSpan={2}>Cavi, Quadri Elettrici e Strutture di fissaggio</td>
              <td className="py-1 px-2.5 text-right">A Corpo</td>
            </tr>
          </tbody>
        </table>

        <p className="mt-2 text-[11px] text-gray-600">{DISCLAIMER}</p>

        <section className="mt-4 grid grid-cols-3 -space-x-px break-inside-avoid">
          <Field label="Totale fornitura al netto">{eur(netTotal)}</Field>
          <Field label={`IVA ${pct(quote.vat_pct)}`}>{eur(vatAmount)}</Field>
          <div className="border-2 border-[#0A1F44] px-2.5 py-1.5">
            <p className="text-[9px] font-bold uppercase tracking-wider text-gray-500">Totale lordo</p>
            <p className="text-lg font-montserrat font-bold">{eur(grossTotal)}</p>
          </div>
        </section>

        <PaymentSection payment={calc.payment} companyIban={quote.company_iban} />

        {(quote.notes || quote.footer_notes) && (
          <section className="mt-4 text-[12px] text-gray-600 space-y-1">
            {quote.notes && <p className="whitespace-pre-wrap">{quote.notes}</p>}
            {quote.footer_notes && <p className="text-[11px] text-gray-400 whitespace-pre-wrap">{quote.footer_notes}</p>}
          </section>
        )}

        <Field label="Firma cliente per accettazione" className="mt-5 min-h-[3.5rem] break-inside-avoid">
          {" "}
        </Field>

        <p className="mt-3 text-center text-[11px] font-bold border border-gray-300 px-3 py-2">
          {NEGATIVE_SURVEY}
        </p>

        <div className="mt-6 pt-3 border-t border-gray-100 flex justify-between text-[10px] text-gray-400">
          <span>ENERGEIDE s.r.l. · {SITE.vat}</span>
          <span className="font-mono tracking-wider">Rif. {quote.code}</span>
        </div>
      </article>

      {/* ------------------------------------------- CONDIZIONI GENERALI */}
      <article
        className="contract bg-white mt-6 p-8 sm:p-10 shadow-sm border border-gray-100 print:mt-0 print:shadow-none print:border-0 print:p-0 text-[12px] leading-snug text-justify"
        style={{ breakBefore: "page" }}
      >
        <h2 className="text-center text-sm font-montserrat font-bold mb-3">{CGC_TITLE}</h2>
        <p className="mb-2">{CGC_INTRO}</p>
        {CGC_SECTIONS.map((s) => (
          <section key={s.title} className="mb-2">
            <h3 className="font-bold mt-3 mb-1">{s.title}</h3>
            {s.paragraphs.map((p, i) => (
              <p key={i} className="mb-1">{p}</p>
            ))}
            {s.contacts && (
              <div className="mt-1 space-y-0.5">
                <p className="font-semibold">Per Energeide s.r.l.:</p>
                <p>• Indirizzo e-mail ordinaria: {SITE.email}</p>
                <p>• Indirizzo PEC: {COMPANY_PEC}</p>
                <p className="font-semibold mt-1">Per il Cliente:</p>
                <p>• Indirizzo e-mail ordinaria: {customer.email || "__________________________"}</p>
                <p>• Indirizzo PEC: _____________________________________</p>
                <p>
                  • Indirizzo raccomandata A/R:{" "}
                  {[customer.address, [customer.zip, customer.city].filter(Boolean).join(" ")]
                    .filter(Boolean)
                    .join(", ") || "_____________________________________"}
                </p>
              </div>
            )}
          </section>
        ))}

        <p className="font-bold mt-4">Allegati:</p>
        {CGC_ATTACHMENTS.map((a) => (
          <p key={a}>{a}</p>
        ))}

        <SignatureBlock place={customer.city} date={date} />

        <p className="mt-6">{CGC_APPROVAL}</p>
        <p className="mt-2 font-semibold">Letto, confermato e sottoscritto.</p>
        <SignatureBlock place={customer.city} date={date} />
      </article>

      {/* ------------------------------------------------------ PRIVACY */}
      <article
        className="contract bg-white mt-6 p-8 sm:p-10 shadow-sm border border-gray-100 print:mt-0 print:shadow-none print:border-0 print:p-0 text-[12px] leading-snug text-justify"
        style={{ breakBefore: "page" }}
      >
        <h2 className="text-center text-sm font-montserrat font-bold mb-3">{PRIVACY_TITLE}</h2>
        {PRIVACY_PARAGRAPHS.map((p, i) =>
          typeof p === "string" ? (
            <p key={i} className="mb-1">{p}</p>
          ) : (
            <h3 key={i} className="font-bold mt-3 mb-1">{p.heading}</h3>
          )
        )}
        <div className="mt-8 grid grid-cols-2 gap-10 text-xs">
          <div className="col-start-2">
            <p className="font-semibold">Firma del Cliente per presa visione</p>
            <div className="h-10 border-b border-gray-400" />
          </div>
        </div>
      </article>
    </div>
  );
};

export default QuoteDocument;
